import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useReducedMotion, type PanInfo } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewSlides, type PreviewSlide } from '../data/productPreview';
import { useCarousel } from '../hooks/useCarousel';
const EASE = [0.23, 1, 0.32, 1] as const;
const SLIDE_WIDTH = 'w-[78vw] max-w-[340px] sm:w-[360px] sm:max-w-none';
export function ProductPeek() {
  const sectionRef = useRef<HTMLElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const dragged = useRef(false);
  const [width, setWidth] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [dragging, setDragging] = useState(false);
  const reduce = useReducedMotion() ?? false;
  const inView = useInView(sectionRef, {
    amount: 0.4
  });
  const paused = hovered || focused || dragging || !inView || reduce;
  const {
    index,
    go,
    goTo,
    offsetOf
  } = useCarousel(previewSlides.length, 4000, paused);
  useEffect(() => {
    const el = spacerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const onDragEnd = (_: unknown, info: PanInfo) => {
    setDragging(false);
    if (info.offset.x < -50 || info.velocity.x < -400) go(1);else if (info.offset.x > 50 || info.velocity.x > 400) go(-1);
    window.setTimeout(() => dragged.current = false, 0);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      go(1);
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      go(-1);
    }
  };
  const arrowClass = 'absolute top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-ink shadow-soft transition-colors duration-150 hover:bg-cloud-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral sm:flex';
  return <section ref={sectionRef} aria-labelledby="peek-heading" className="w-full overflow-hidden bg-cloud py-12 sm:py-16" data-id="element-241" data-mp="1ktyjbieikdjr">
      <div className="mx-auto max-w-6xl px-5 text-center" data-id="element-242" data-mp="1ftk7rqdpelgz">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-coral-deep" data-id="element-243" data-mp="1aviob4cd8k4z">Look inside</p>
        <h2 id="peek-heading" className="mt-3 font-display text-3xl font-extrabold tracking-tight text-ink sm:text-4xl" data-id="element-244" data-mp="16uyw8f6ng6pp">
          Real pages you’ll run at practice
        </h2>
        <p className="mx-auto mt-3 max-w-xl text-[15px] text-slate sm:text-base" data-id="element-245" data-mp="1ylmvflorj20q">
          Print-ready, one idea per page — swipe through a few from Volume 4.
        </p>
      </div>

      <div role="region" aria-roledescription="carousel" aria-label="Product pages preview" tabIndex={0} onKeyDown={onKeyDown} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} onFocus={() => setFocused(true)} onBlur={e => {
      if (!e.currentTarget.contains(e.relatedTarget as Node)) setFocused(false);
    }} className="relative mx-auto mt-8 max-w-[640px] focus-visible:outline-none sm:mt-10" data-id="element-246" data-mp="1tsjvmvte6wc3">
        <motion.div drag="x" dragConstraints={{
        left: 0,
        right: 0
      }} dragElastic={0.18} dragSnapToOrigin onDragStart={() => {
        dragged.current = true;
        setDragging(true);
      }} onDragEnd={onDragEnd} style={{
        touchAction: 'pan-y'
      }} className="relative cursor-grab active:cursor-grabbing" data-id="element-247" data-mp="d2ssvqs9nbih">
          {/* Sizes the stage to one 9:16 slide so the layout never jumps. */}
          <div ref={spacerRef} aria-hidden="true" className={`invisible mx-auto aspect-[9/16] ${SLIDE_WIDTH}`} data-id="element-248" data-mp="wr1uerfp8tun" />

          <ul aria-live={paused ? 'polite' : 'off'} className="absolute inset-0" data-id="element-249" data-mp="10u7yptj94ayh">
            {previewSlides.map((slide, i) => <Slide key={slide.src} slide={slide} position={i} offset={offsetOf(i)} width={width} reduce={reduce} onSelect={() => {
            if (!dragged.current) goTo(i);
          }} data-id="element-250" data-mp="1aq1ju1b1dcrv" data-mpi={i} />)}
          </ul>
        </motion.div>

        <button type="button" onClick={() => go(-1)} aria-label="Previous page" className={`${arrowClass} left-3`} data-id="element-251" data-mp="16suh3t6l653s">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-252" data-mp="11v2n8tmds8wy" />
        </button>
        <button type="button" onClick={() => go(1)} aria-label="Next page" className={`${arrowClass} right-3`} data-id="element-253" data-mp="e58dikxcz0gx">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-254" data-mp="97gez827kg3r" />
        </button>
      </div>

      <div className="mx-auto mt-6 max-w-6xl px-5 text-center" data-id="element-255" data-mp="o47erifsnttw">
        <p className="min-h-[20px] text-sm font-semibold text-ink" data-id="element-256" data-mp="j2rrdjq59b8c">{previewSlides[index].label}</p>
        <div className="mt-3 flex items-center justify-center gap-1" role="group" aria-label="Choose a page" data-id="element-257" data-mp="1zh03slh7a469">
          {previewSlides.map((slide, i) => <button key={slide.src} type="button" onClick={() => goTo(i)} aria-label={`Go to slide ${i + 1}`} aria-current={index === i ? 'true' : undefined} className="flex h-6 items-center rounded-full px-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral" data-id="element-258" data-mp="222h9yojm2ubt" data-mpi={i}>
              <span className={`block h-2 rounded-full transition-[width,background-color] duration-200 ${index === i ? 'w-6 bg-coral' : 'w-2 bg-ink/20'}`} data-id="element-259" data-mp="1x5h4oful5vlr" />
            </button>)}
        </div>
      </div>
    </section>;
}
type SlideProps = {
  slide: PreviewSlide;
  position: number;
  offset: number;
  width: number;
  reduce: boolean;
  onSelect: () => void;
};
function Slide({
  slide,
  position,
  offset,
  width,
  reduce,
  onSelect
}: SlideProps) {
  const prevOffset = useRef(offset);
  // A slide wrapping from one end to the other is hidden, so it jumps instead of sweeping across.
  const wraps = Math.abs(offset - prevOffset.current) > 2;
  useEffect(() => {
    prevOffset.current = offset;
  }, [offset]);
  const active = offset === 0;
  const neighbor = Math.abs(offset) === 1;
  const gap = width > 340 ? 24 : 12;
  return <motion.li aria-roledescription="slide" aria-label={`Slide ${position + 1} of ${previewSlides.length}`} aria-hidden={!active} initial={false} animate={{
    x: offset * (width * 0.9 + gap),
    scale: active ? 1 : 0.86,
    opacity: active ? 1 : neighbor ? 0.5 : 0
  }} transition={wraps || reduce ? {
    duration: 0
  } : {
    duration: 0.3,
    ease: EASE
  }} style={{
    left: `calc(50% - ${width / 2}px)`,
    zIndex: active ? 10 : neighbor ? 5 : 0
  }} className={`absolute top-0 aspect-[9/16] ${SLIDE_WIDTH} ${active ? '' : 'cursor-pointer'}`} onClick={active ? undefined : onSelect} data-id="element-260" data-mp="39fyaq29scmce">
      <img src={slide.src} alt={slide.alt} width={1080} height={1920} draggable={false} loading={position === 0 ? 'eager' : 'lazy'} className="h-full w-full select-none rounded-2xl bg-white object-contain shadow-book ring-1 ring-ink/10" data-id="element-261" data-mp="2bptokb6jyai9" />
    </motion.li>;
}